import { apiRequest, getRestErrorMessage } from 'common-src/features/rest';
import { HttpMethods } from 'common-src/features/rest/constants';

const DEFAULT_TASK_QUEUE_PAGE_SIZE = 200;
const DEFAULT_CARE_ORCHESTRATION_TRIGGER_PAYLOAD = {
  type_id: 11,
  reason_id: 12,
  subject: 'Care Coordination Task',
  status: 1,
  priority: 'high',
  team: 'ops',
};

const extractResponseBody = async (response) => {
  const body = await response.json();

  if (!response.ok) {
    throw new Error(getRestErrorMessage(body) || `Request failed with status ${response.status}`);
  }

  return body;
};

const assertOk = async (response) => {
  if (response.ok) return;

  const errorBody = await response.json().catch(() => null);
  throw new Error(
    getRestErrorMessage(errorBody) || `Request failed with status ${response.status}`,
  );
};

export const fetchTaskQueueData = async (pageSize = DEFAULT_TASK_QUEUE_PAGE_SIZE) => {
  const taskQueueFilters = {
    limit: pageSize,
    offset: 0,
  };

  const fetchPage = async ({
    currentOffset = 0,
    taskQueueRows = [],
    taskQueueMeta = null,
    total = null,
  }) => {
    const taskQueueRes = await apiRequest({
      endpoint: '../v2/dashboard/task-queue',
      queryParams: { ...taskQueueFilters, limit: pageSize, offset: currentOffset },
    });
    const taskQueueBody = await extractResponseBody(taskQueueRes);

    const nextTaskQueueMeta = taskQueueBody?.meta || taskQueueMeta;
    const batch = taskQueueBody?.data || [];
    const nextTaskQueueRows = [...taskQueueRows, ...batch];
    const nextTotal = total == null ? (taskQueueBody?.meta?.total ?? batch.length) : total;

    if (!batch.length || nextTaskQueueRows.length >= nextTotal) {
      return { taskQueueRows: nextTaskQueueRows, taskQueueMeta: nextTaskQueueMeta };
    }

    return fetchPage({
      currentOffset: currentOffset + pageSize,
      taskQueueRows: nextTaskQueueRows,
      taskQueueMeta: nextTaskQueueMeta,
      total: nextTotal,
    });
  };

  return fetchPage({});
};

export const fetchDashboardSupplementalData = async () => {
  const [appointmentsRes, statsRes] = await Promise.all([
    apiRequest({ endpoint: '../v2/dashboard/appointments' }),
    apiRequest({ endpoint: '../v2/dashboard/stats' }),
  ]);

  const [appointmentsBody, statsBody] = await Promise.all([
    extractResponseBody(appointmentsRes),
    extractResponseBody(statsRes),
  ]);

  return { appointmentsBody, statsBody };
};

export const assignTaskToMe = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/assign-me`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const blockTask = async (taskId, blockedReason) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/block`,
    method: HttpMethods.Post,
    body: { blockedReason },
  });
  await assertOk(response);
};

export const markTaskComplete = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/mark-complete`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const unblockTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/unblock`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const unassignTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/unassign`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const reassignTask = async (taskId, assigneeId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/assign`,
    method: HttpMethods.Post,
    body: { assigneeId },
  });
  await assertOk(response);
};

export const escalateTask = async (taskId, escalatedReason, escalatedTo) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/escalate`,
    method: HttpMethods.Post,
    body: { escalatedReason, escalatedTo },
  });
  await assertOk(response);
};

export const deescalateTask = async (taskId) => {
  const response = await apiRequest({
    endpoint: `../v2/dashboard/task-queue/${taskId}/deescalate`,
    method: HttpMethods.Post,
  });
  await assertOk(response);
};

export const triggerCareOrchestrationWorkflow = async (patientId, payload = {}) => {
  if (!patientId) {
    throw new Error('Patient ID is required');
  }

  const response = await apiRequest({
    endpoint: '../v2/dashboard/task-queue/trigger-care-orchestration-workflow',
    method: HttpMethods.Post,
    body: {
      patient_id: patientId,
      ...DEFAULT_CARE_ORCHESTRATION_TRIGGER_PAYLOAD,
      ...payload,
    },
  });

  return extractResponseBody(response);
};
